/**
 * Pivot Table Data Extractor for AI Insights
 *
 * Extracts data from Excel Pivot Tables and transforms it into the ExtractedTableData
 * format compatible with the AI Insights feature.
 *
 * Uses Office.js PivotLayout methods:
 * - getDataBodyRange(): Gets the data values area
 * - getRowLabelRange(): Gets row labels (leftmost columns)
 * - getColumnLabelRange(): Gets column headers
 * - getFilterAxisRange(): Gets filter area (optional)
 *
 * @module pivotTableDataExtractor
 * @see {@link ../docs/PIVOT_TABLE_EXTRACTOR.md} for comprehensive documentation
 */

import { ExtractedTableData } from "./selectedDataExtractor";

/**
 * Detects if the current user selection overlaps with any Pivot Table on the active worksheet.
 *
 * Uses rectangle intersection logic to determine if the selected range overlaps with any
 * Pivot Table's bounding box. This allows detection even when the user selects only a
 * portion of the Pivot Table.
 *
 * @returns {Promise<Excel.PivotTable | null>} The first Pivot Table that overlaps with the
 *          selection, or null if no overlap is found.
 *
 * @example
 * ```typescript
 * const pivotTable = await findPivotTableInSelection();
 * if (pivotTable) {
 *   console.log(`Found pivot table: ${pivotTable.name}`);
 *   const data = await extractDataFromPivotTable(pivotTable);
 * }
 * ```
 *
 * @throws {Error} If there's an issue accessing the Excel API (logged, not thrown)
 */
export async function findPivotTableInSelection(): Promise<Excel.PivotTable | null> {
  let foundPivotTable: Excel.PivotTable | null = null;

  await Excel.run(async (context) => {
    try {
      const selectedRange = context.workbook.getSelectedRange();
      selectedRange.load(["address", "rowIndex", "columnIndex", "rowCount", "columnCount"]);

      const worksheet = selectedRange.worksheet;
      worksheet.load("name");

      const pivotTables = worksheet.pivotTables;
      pivotTables.load("items");
      await context.sync();

      if (pivotTables.items.length === 0) {
        console.log("No Pivot Tables found on this worksheet");
        return;
      }

      // Check each pivot table to see if selection overlaps with it
      for (const pivotTable of pivotTables.items) {
        pivotTable.load("name");

        const pivotLayout = pivotTable.layout;
        const pivotRange = pivotLayout.getRange();
        pivotRange.load(["address", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
        await context.sync();

        // Check if selection overlaps with pivot table range
        const selStartRow = selectedRange.rowIndex;
        const selEndRow = selectedRange.rowIndex + selectedRange.rowCount - 1;
        const selStartCol = selectedRange.columnIndex;
        const selEndCol = selectedRange.columnIndex + selectedRange.columnCount - 1;

        const pivotStartRow = pivotRange.rowIndex;
        const pivotEndRow = pivotRange.rowIndex + pivotRange.rowCount - 1;
        const pivotStartCol = pivotRange.columnIndex;
        const pivotEndCol = pivotRange.columnIndex + pivotRange.columnCount - 1;

        const rowOverlap = selStartRow <= pivotEndRow && selEndRow >= pivotStartRow;
        const colOverlap = selStartCol <= pivotEndCol && selEndCol >= pivotStartCol;

        if (rowOverlap && colOverlap) {
          console.log(`✅ Selection overlaps with Pivot Table: ${pivotTable.name}`);
          foundPivotTable = pivotTable;
          break;
        }
      }
    } catch (error) {
      console.error("Error finding Pivot Table in selection:", error);
    }
  });

  return foundPivotTable;
}

/**
 * Extracts data from a Pivot Table and converts it to ExtractedTableData format
 * @returns ExtractedTableData compatible with AI Insights, or null if extraction fails
 */
export async function extractPivotTableData(): Promise<ExtractedTableData | null> {
  let tableData: ExtractedTableData | null = null;

  await Excel.run(async (context) => {
    try {
      const selectedRange = context.workbook.getSelectedRange();
      selectedRange.load(["address"]);

      const worksheet = selectedRange.worksheet;
      worksheet.load("name");

      const pivotTables = worksheet.pivotTables;
      pivotTables.load("items");
      await context.sync();

      if (pivotTables.items.length === 0) {
        console.log("📊 No Pivot Tables found on this worksheet");
        return;
      }

      // For now, use the first pivot table on the worksheet
      // Could be enhanced to detect which pivot table the selection is in
      const pivotTable = pivotTables.items[0];
      pivotTable.load("name");
      await context.sync();

      console.log(`📊 Extracting data from Pivot Table: ${pivotTable.name}`);

      const pivotLayout = pivotTable.layout;

      // Get the different ranges of the pivot table
      const dataBodyRange = pivotLayout.getDataBodyRange();
      const columnLabelRange = pivotLayout.getColumnLabelRange();

      // Row labels might not exist if there are no row fields
      let rowLabelRange: Excel.Range | null = null;
      let hasRowLabels = false;

      try {
        rowLabelRange = pivotLayout.getRowLabelRange();
        rowLabelRange.load(["values", "rowCount", "columnCount", "address"]);
        hasRowLabels = true;
      } catch (e) {
        console.log("No row labels in pivot table (no row fields)");
      }

      // Load values from ranges
      dataBodyRange.load(["values", "rowCount", "columnCount", "address"]);
      columnLabelRange.load(["values", "rowCount", "columnCount", "address"]);

      await context.sync();

      console.log("📊 Pivot Table ranges loaded:");
      console.log(`   Data Body: ${dataBodyRange.address} (${dataBodyRange.rowCount} rows x ${dataBodyRange.columnCount} cols)`);
      console.log(`   Column Labels: ${columnLabelRange.address}`);
      if (hasRowLabels && rowLabelRange) {
        console.log(`   Row Labels: ${rowLabelRange.address} (${rowLabelRange.rowCount} rows x ${rowLabelRange.columnCount} cols)`);
      }

      // Build headers array
      const headers: string[] = [];

      // Add row label headers (if any)
      if (hasRowLabels && rowLabelRange && rowLabelRange.values) {
        // Get the header row from row labels (typically the first row)
        // Row fields are in the header area
        const rowFieldCount = rowLabelRange.columnCount;

        // Try to get row field names from the pivot table
        const rowHierarchies = pivotTable.rowHierarchies;
        rowHierarchies.load("items/name");
        await context.sync();

        for (let i = 0; i < rowFieldCount; i++) {
          if (rowHierarchies.items[i]) {
            headers.push(String(rowHierarchies.items[i].name || `Row Field ${i + 1}`));
          } else {
            headers.push(`Row Field ${i + 1}`);
          }
        }
      }

      // Add data column headers from column labels
      // Column labels can be multi-row (for nested column fields)
      // We'll use the last row of column labels as the main headers
      if (columnLabelRange.values && columnLabelRange.values.length > 0) {
        const lastColumnLabelRow = columnLabelRange.values[columnLabelRange.values.length - 1];
        for (let i = 0; i < lastColumnLabelRow.length; i++) {
          const headerValue = lastColumnLabelRow[i];
          headers.push(String(headerValue !== null && headerValue !== undefined ? headerValue : `Value ${i + 1}`));
        }
      }

      console.log("📊 Extracted headers:", headers);

      // Build data rows
      const rows: any[][] = [];
      const dataRowCount = dataBodyRange.rowCount;

      for (let rowIdx = 0; rowIdx < dataRowCount; rowIdx++) {
        const row: any[] = [];

        // Add row label values (if any)
        if (hasRowLabels && rowLabelRange && rowLabelRange.values) {
          const rowLabelRow = rowLabelRange.values[rowIdx];
          if (rowLabelRow) {
            for (let colIdx = 0; colIdx < rowLabelRange.columnCount; colIdx++) {
              const cellValue = rowLabelRow[colIdx];
              row.push(cellValue !== null && cellValue !== undefined ? cellValue : null);
            }
          }
        }

        // Add data body values
        if (dataBodyRange.values && dataBodyRange.values[rowIdx]) {
          const dataRow = dataBodyRange.values[rowIdx];
          for (let colIdx = 0; colIdx < dataRow.length; colIdx++) {
            const cellValue = dataRow[colIdx];
            row.push(cellValue !== null && cellValue !== undefined ? cellValue : null);
          }
        }

        // Only add non-empty rows
        if (row.some(cell => cell !== null && cell !== "" && cell !== undefined)) {
          rows.push(row);
        }
      }

      console.log(`📊 Extracted ${rows.length} data rows`);

      if (headers.length > 0 && rows.length > 0) {
        tableData = { headers, rows };
        console.log("✅ Successfully extracted Pivot Table data:", {
          headers: headers.length,
          rows: rows.length,
          sampleHeaders: headers.slice(0, 5),
          sampleRow: rows[0]?.slice(0, 5)
        });
      } else {
        console.warn("⚠️ No data extracted from Pivot Table");
      }

    } catch (error) {
      console.error("❌ Error extracting Pivot Table data:", error);
    }
  });

  return tableData;
}

/**
 * Extracts data from a specific Pivot Table (when selection overlaps multiple)
 * @param pivotTable The specific PivotTable to extract data from
 * @returns ExtractedTableData compatible with AI Insights
 */
export async function extractDataFromPivotTable(pivotTable: Excel.PivotTable): Promise<ExtractedTableData | null> {
  let tableData: ExtractedTableData | null = null;

  await Excel.run(async (context) => {
    try {
      // Re-get the pivot table in this context
      pivotTable.load("name");
      await context.sync();

      console.log(`📊 Extracting data from specific Pivot Table: ${pivotTable.name}`);

      const pivotLayout = pivotTable.layout;

      // Get ranges
      const dataBodyRange = pivotLayout.getDataBodyRange();
      const columnLabelRange = pivotLayout.getColumnLabelRange();

      let rowLabelRange: Excel.Range | null = null;
      let hasRowLabels = false;

      try {
        rowLabelRange = pivotLayout.getRowLabelRange();
        rowLabelRange.load(["values", "rowCount", "columnCount"]);
        hasRowLabels = true;
      } catch (e) {
        console.log("No row labels in pivot table");
      }

      dataBodyRange.load(["values", "rowCount", "columnCount"]);
      columnLabelRange.load(["values", "rowCount", "columnCount"]);

      // Get hierarchy names for better headers
      const rowHierarchies = pivotTable.rowHierarchies;
      const columnHierarchies = pivotTable.columnHierarchies;
      const dataHierarchies = pivotTable.dataHierarchies;

      rowHierarchies.load("items/name");
      columnHierarchies.load("items/name");
      dataHierarchies.load("items/name");

      await context.sync();

      // Build headers
      const headers: string[] = [];

      // Row field headers
      if (hasRowLabels && rowLabelRange) {
        for (let i = 0; i < rowLabelRange.columnCount; i++) {
          if (rowHierarchies.items[i]) {
            headers.push(String(rowHierarchies.items[i].name));
          } else {
            headers.push(`Row ${i + 1}`);
          }
        }
      }

      // Column/Value headers
      if (columnLabelRange.values && columnLabelRange.values.length > 0) {
        const lastLabelRow = columnLabelRange.values[columnLabelRange.values.length - 1];
        for (let i = 0; i < lastLabelRow.length; i++) {
          const val = lastLabelRow[i];
          headers.push(String(val !== null && val !== undefined ? val : `Value ${i + 1}`));
        }
      }

      // Build rows
      const rows: any[][] = [];

      for (let rowIdx = 0; rowIdx < dataBodyRange.rowCount; rowIdx++) {
        const row: any[] = [];

        // Row labels
        if (hasRowLabels && rowLabelRange && rowLabelRange.values[rowIdx]) {
          for (const cell of rowLabelRange.values[rowIdx]) {
            row.push(cell !== null && cell !== undefined ? cell : null);
          }
        }

        // Data values
        if (dataBodyRange.values[rowIdx]) {
          for (const cell of dataBodyRange.values[rowIdx]) {
            row.push(cell !== null && cell !== undefined ? cell : null);
          }
        }

        if (row.some(c => c !== null && c !== "" && c !== undefined)) {
          rows.push(row);
        }
      }

      if (headers.length > 0 && rows.length > 0) {
        tableData = { headers, rows };
      }

    } catch (error) {
      console.error("Error extracting from specific Pivot Table:", error);
    }
  });

  return tableData;
}

/**
 * Extracts only the user-selected portion of a Pivot Table with proper headers.
 *
 * This function intelligently extracts the selected portion while maintaining the pivot
 * table structure:
 * 1. Gets the actual column headers from the pivot table (not from selection)
 * 2. Gets the row labels for selected rows (e.g., Product names, Categories)
 * 3. Gets the data values for selected rows only
 *
 * This ensures the backend receives proper context even when user selects rows from
 * the middle of the pivot table.
 *
 * @returns {Promise<ExtractedTableData | null>} Extracted data with proper headers and
 *          selected rows, or null if extraction fails.
 *
 * @example
 * ```typescript
 * // Pivot table structure:
 * // Headers:  | Product | Q1 Sales | Q2 Sales |
 * // Row 1:    | Widget A | 1000    | 1200     |
 * // Row 2:    | Widget B | 800     | 900      |  <- User selects this row
 * // Row 3:    | Widget C | 1500    | 1600     |
 *
 * // Result includes headers + selected row:
 * // { headers: ["Product", "Q1 Sales", "Q2 Sales"],
 * //   rows: [["Widget B", 800, 900]] }
 * ```
 */
export async function extractSelectedPivotData(): Promise<ExtractedTableData | null> {
  let tableData: ExtractedTableData | null = null;

  await Excel.run(async (context) => {
    try {
      const selectedRange = context.workbook.getSelectedRange();
      selectedRange.load(["address", "values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);

      const worksheet = selectedRange.worksheet;
      const pivotTables = worksheet.pivotTables;
      pivotTables.load("items");
      await context.sync();

      if (pivotTables.items.length === 0) {
        console.log("⚠️ No pivot table found, using simple extraction");
        // Fallback to simple extraction
        const values = selectedRange.values;
        if (values && values.length > 0) {
          const headers = values[0].map((h, idx) => String(h || `Column ${idx + 1}`));
          const rows = values.slice(1).filter(row =>
            row.some(cell => cell !== null && cell !== "" && cell !== undefined)
          );
          tableData = { headers, rows };
        }
        return;
      }

      // Get the first pivot table (or find the one that overlaps with selection)
      const pivotTable = pivotTables.items[0];
      pivotTable.load("name");

      const pivotLayout = pivotTable.layout;
      const pivotRange = pivotLayout.getRange();
      pivotRange.load(["rowIndex", "columnIndex", "rowCount", "columnCount"]);

      // Get pivot table ranges
      const dataBodyRange = pivotLayout.getDataBodyRange();
      const columnLabelRange = pivotLayout.getColumnLabelRange();

      let rowLabelRange: Excel.Range | null = null;
      let hasRowLabels = false;

      try {
        rowLabelRange = pivotLayout.getRowLabelRange();
        rowLabelRange.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
        hasRowLabels = true;
      } catch (e) {
        console.log("No row labels in pivot table");
      }

      dataBodyRange.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
      columnLabelRange.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);

      await context.sync();

      console.log(`📊 Extracting selected portion from Pivot Table: ${pivotTable.name}`);
      console.log(`   Selection: ${selectedRange.address}`);
      console.log(`   Pivot Range: ${pivotRange.rowIndex}:${pivotRange.rowIndex + pivotRange.rowCount - 1}`);

      // BUILD HEADERS (always include full headers from pivot table)
      const headers: string[] = [];

      // Add row field headers
      if (hasRowLabels && rowLabelRange) {
        const rowHierarchies = pivotTable.rowHierarchies;
        rowHierarchies.load("items/name");
        await context.sync();

        for (let i = 0; i < rowLabelRange.columnCount; i++) {
          headers.push(rowHierarchies.items[i]?.name || `Row Field ${i + 1}`);
        }
      }

      // Add data column headers (from last row of column labels)
      if (columnLabelRange.values && columnLabelRange.values.length > 0) {
        const lastLabelRow = columnLabelRange.values[columnLabelRange.values.length - 1];
        for (let i = 0; i < lastLabelRow.length; i++) {
          headers.push(String(lastLabelRow[i] !== null && lastLabelRow[i] !== undefined ? lastLabelRow[i] : `Value ${i + 1}`));
        }
      }

      console.log(`   Headers: ${headers.join(", ")}`);

      // EXTRACT SELECTED ROWS ONLY
      // Calculate which rows of the data body are selected
      const selStartRow = selectedRange.rowIndex;
      const selEndRow = selectedRange.rowIndex + selectedRange.rowCount - 1;

      const dataBodyStartRow = dataBodyRange.rowIndex;
      const dataBodyEndRow = dataBodyRange.rowIndex + dataBodyRange.rowCount - 1;

      // Calculate overlap between selection and data body
      const extractStartRow = Math.max(selStartRow, dataBodyStartRow);
      const extractEndRow = Math.min(selEndRow, dataBodyEndRow);

      if (extractStartRow > extractEndRow) {
        console.log("⚠️ Selection doesn't overlap with pivot data body");
        return;
      }

      // Calculate the indices within the data body range
      const startIdx = extractStartRow - dataBodyStartRow;
      const endIdx = extractEndRow - dataBodyStartRow;

      console.log(`   Extracting rows ${startIdx} to ${endIdx} from data body (${endIdx - startIdx + 1} rows)`);

      // BUILD ROWS (only selected rows)
      const rows: any[][] = [];

      for (let rowIdx = startIdx; rowIdx <= endIdx; rowIdx++) {
        const row: any[] = [];

        // Add row labels for this row
        if (hasRowLabels && rowLabelRange && rowLabelRange.values[rowIdx]) {
          for (const cell of rowLabelRange.values[rowIdx]) {
            row.push(cell !== null && cell !== undefined ? cell : null);
          }
        }

        // Add data values for this row
        if (dataBodyRange.values[rowIdx]) {
          for (const cell of dataBodyRange.values[rowIdx]) {
            row.push(cell !== null && cell !== undefined ? cell : null);
          }
        }

        // Only add non-empty rows
        if (row.some(c => c !== null && c !== "" && c !== undefined)) {
          rows.push(row);
        }
      }

      if (headers.length > 0 && rows.length > 0) {
        tableData = { headers, rows };
        console.log(`✅ Extracted ${rows.length} selected rows with ${headers.length} columns from pivot table`);
        console.log(`   Sample data:`, { headers: headers.slice(0, 5), firstRow: rows[0]?.slice(0, 5) });
      } else {
        console.warn("⚠️ No valid data extracted from selection");
      }

    } catch (error) {
      console.error("❌ Error extracting selected pivot data:", error);
    }
  });

  return tableData;
}

/**
 * Combined function that first checks for Pivot Table, then extracts data appropriately.
 * This can be used as a drop-in enhancement to the existing extractSelectedData flow.
 *
 * **IMPORTANT CHANGE:** This function now extracts ONLY the user-selected portion of the
 * pivot table, not the entire pivot table. This ensures that backend requests only contain
 * the data the user explicitly selected.
 *
 * @returns ExtractedTableData if a Pivot Table is found and data is extracted, null otherwise
 *
 * @example
 * ```typescript
 * // User selects rows 5-10 of a 100-row pivot table
 * // Only those 6 rows will be sent to the backend, not all 100
 * const data = await extractPivotTableDataIfPresent();
 * if (data) {
 *   console.log(`Sending ${data.rows.length} rows to backend`);
 * }
 * ```
 */
export async function extractPivotTableDataIfPresent(): Promise<ExtractedTableData | null> {
  const pivotTable = await findPivotTableInSelection();

  if (pivotTable) {
    console.log("📊 Pivot Table detected in selection, extracting selected portion only");
    return await extractSelectedPivotData();
  }

  console.log("📊 No Pivot Table in selection");
  return null;
}

/**
 * Gets metadata about the Pivot Table for display purposes
 */
export async function getPivotTableMetadata(): Promise<{
  name: string;
  rowFields: string[];
  columnFields: string[];
  valueFields: string[];
  filterFields: string[];
} | null> {
  let metadata: {
    name: string;
    rowFields: string[];
    columnFields: string[];
    valueFields: string[];
    filterFields: string[];
  } | null = null;

  await Excel.run(async (context) => {
    try {
      const selectedRange = context.workbook.getSelectedRange();
      const worksheet = selectedRange.worksheet;

      const pivotTables = worksheet.pivotTables;
      pivotTables.load("items");
      await context.sync();

      if (pivotTables.items.length === 0) {
        return;
      }

      const pivotTable = pivotTables.items[0];
      pivotTable.load("name");

      const rowHierarchies = pivotTable.rowHierarchies;
      const columnHierarchies = pivotTable.columnHierarchies;
      const dataHierarchies = pivotTable.dataHierarchies;
      const filterHierarchies = pivotTable.filterHierarchies;

      rowHierarchies.load("items/name");
      columnHierarchies.load("items/name");
      dataHierarchies.load("items/name");
      filterHierarchies.load("items/name");

      await context.sync();

      metadata = {
        name: pivotTable.name,
        rowFields: rowHierarchies.items.map(h => h.name),
        columnFields: columnHierarchies.items.map(h => h.name),
        valueFields: dataHierarchies.items.map(h => h.name),
        filterFields: filterHierarchies.items.map(h => h.name)
      };

      console.log("📊 Pivot Table Metadata:", metadata);

    } catch (error) {
      console.error("Error getting Pivot Table metadata:", error);
    }
  });

  return metadata;
}
